import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  NgZone,
} from "@angular/core";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import * as _ from "lodash";
import { FormControl } from "@angular/forms";

export type MultipleOptions = Array<{
  label: string;
  value: any;
  isGroupOption?: boolean;
  parentValue?: any;
  hide?: boolean;
}>;

export enum SelectLabelType {
  Default = "default",
  Badge = "badge",
}
@Component({
  selector: "multiple-select",
  templateUrl: "./multiple-select.component.html",
  styleUrls: ["./multiple-select.component.scss"],
})
export class MultipleSelect implements OnInit, OnChanges {
  @Input() isGroupOption: boolean = false;
  @Input() allOptionValue: any = -1;
  @Input() placeholder: string;
  @Input() noSelectCount: boolean = false;
  @Input() selected: any = [];
  selectedControl: FormControl = new FormControl([-1]);
  @Input() options: MultipleOptions;
  @Input() canSelectAll: boolean;
  @Input() canSearch: boolean;
  @Input() selectLabelType: SelectLabelType = SelectLabelType.Default;

  @Output() selectedChange = new EventEmitter();

  private subjecValueSearch = new Subject<any>();

  formControl = new FormControl(new Date());

  filteredOptions = [];
  allOptions = [];
  isCheckAll = false;
  searchInput = "";
  public SelectLabelType = SelectLabelType;
  public badgeSelected = [];
  containerWidth = 0;


  @ViewChild('multiSelectContainerRef', { static: true }) multiSelectContainerRef!: ElementRef;
  private resizeObserver!: any;
  private resizeSubject = new Subject<number>();
  private destroy$ = new Subject<void>();

  get placeholderText() {
    if (this.selectLabelType == SelectLabelType.Badge) return this.placeholder;
    return this.placeholder + (!this.noSelectCount ? ': None' : '');
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.options &&
      changes.options.currentValue !== changes.options.previousValue
    ) {
      this.allOptions = !this.isGroupOption
        ? this.options.map((item) => item.value)
        : this.options
          .filter((item) => !item.isGroupOption)
          .map((item) => item.value);

      this.filteredOptions = [...(this.options || [])];

    }
    if(changes.selected && changes.selected.currentValue !== this.selectedControl.value){
      setTimeout(() => { this.selectedControl.setValue([...this.selected]) }, 0);
      if (this.selectLabelType == SelectLabelType.Badge) {
        this.subscribeResizeObserver();
      }
    }
  }
  ngOnInit() {
    this.selectedControl.valueChanges.subscribe((value) => {
      const hasAllValue = value.includes(this.allOptionValue);
      const selectWithoutAllValue = value.filter((item) => item !== this.allOptionValue);
      if (!_.isEqual(selectWithoutAllValue, this.selected)) { // Avoid unnecessary trigger
        this.selectedChange.emit(selectWithoutAllValue);
      }
      if (value.length == this.allOptions.length && !hasAllValue) {
        this.selectedControl.setValue([...this.allOptions, this.allOptionValue])
        this.isCheckAll = true
      } else if(this.isCheckAll){
        this.isCheckAll = false;
        this.selectedControl.setValue(selectWithoutAllValue)
      }
    });
  }

  ngOnDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  subscribeResizeObserver() {
    const container = this.multiSelectContainerRef.nativeElement;

    this.resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        this.ngZone.run(() => {
          this.resizeSubject.next(entry.contentRect.width);
        });
      }
    });

    this.resizeObserver.observe(container);

    this.resizeSubject.pipe(
      debounceTime(200),
      takeUntil(this.destroy$)
    ).subscribe(width => {
      this.containerWidth = width;
      this.handleCalcBadgeSelected();
    });

  }

  handleCalcBadgeSelected() {
    const selected = this.selectedControl.value || [];

    let containWidth = this.containerWidth - 40
    const badgeSelected = [];

    for (let i = 0; i < selected.length + 1; i++) {
      const item = this.filteredOptions.find((el) => el.value === selected[i]);
      if (item) {
        const itemWidth = item.label.length * 8 + 40;
        if (itemWidth < containWidth) {
          containWidth -= itemWidth;
          badgeSelected.push({
            label: item.label,
            value: item.value,
            type: 'value'
          });
        } else {
          badgeSelected.push({
            label: `+${selected.length - i}`,
            type: 'other'
          });
          break;
        }
      }
    }

    this.badgeSelected = badgeSelected;
    this.cdr.markForCheck();
  }

  onSelectClick() {
    setTimeout(() => {
      const listContainer = document.getElementsByClassName("options-list");
      listContainer.length && listContainer[0].scroll({ top: 0 });
    }, 50); // TODO Temporarily solve the problem of scrolling incorrectly during the first render, please check
    if (this.canSearch) {
      if (this.searchInput) {
        this.searchInput = "";
        this.subjecValueSearch.next("");
      }
    }
  }

  ngAfterViewInit() {
    this.subjecValueSearch
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe((value) => { this.searchOptions(value) });
  }

  searchOptions(searchString: string) {
    if(searchString) searchString = searchString.trim();
    if (!searchString) this.filteredOptions = [...(this.options || [])];
    else {
      if (this.isGroupOption) {
        this.filteredOptions = this.options.map((item) => {
          if (item.label.toLowerCase().includes(searchString.toLowerCase()) || item.isGroupOption) return item;
          else {
            return { ...item, hide: true };
          }
        });
      } else {
        this.filteredOptions = this.options.map((item) => {
          if (item.label.toLowerCase().includes(searchString.toLowerCase())) return item;
          else {
            return { ...item, hide: true };
          }
        });
      }
      // const allFilteredValue = this.filteredOptions.filter((item) => !item.isGroupOption).map((item) => item.value)
      // this.cloneSelected = this.cloneSelected.filter((el) => !allFilteredValue.includes(el));
    }
    setTimeout(() => this.selectedControl.setValue([...this.selected]), 0);
  }
  handleSearch(searchString: string) {
    setTimeout(() => {
      const listContainer = document.getElementById("search-input")
      listContainer && listContainer.focus()
    }, 200);
    this.subjecValueSearch.next(searchString || null);
  }

  selectCheck(event) { }

  onSelect(value) {
    this.handleCalcBadgeSelected();
  }

  onRemoveSelect(event, item) {
    event.stopPropagation();
    const selected = this.selectedControl.value || [];
    const index = selected.indexOf(item.value);
    if (index > -1) {
      selected.splice(index, 1);
      this.selectedControl.setValue([...selected]);
    }
  }

  handleCheckAll() {
    if (this.isCheckAll) this.selectedControl.setValue([]);
    else this.selectedControl.setValue([...this.allOptions])
  }
  getFlatten(arr) {
    function flatten(arr) {
      return arr
        ? arr.reduce(function (flat, toFlatten) {
          return flat.concat(
            Array.isArray(toFlatten) ? flatten(toFlatten) : toFlatten
          );
        }, [])
        : [];
    }
    return flatten(arr);
  }
}
